import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";
import { Dialog, DialogTitle, DialogDescription } from "../components/ui/Dialog";
import { displayLicenseName } from "../lib/display-name";

interface ChangeRow {
  change_id: number | string;
  changed_at: string;
  change_type: string;
  severity: string;
  detected_by: string | null;
  notes: string | null;
  entity_id: string | null;
  entity_type: string | null;
  source: string | null;
  source_id: string | null;
  license_number: string | null;
  display_name: string | null;
  legal_name: string | null;
  dba_name: string | null;
  license_type: string | null;
  place_id: string | null;
  diff: Record<string, unknown> | null;
  is_gbp_change?: boolean;
  gbp_changed_fields?: string[];
}

interface Facet {
  value: string;
  count: number;
}
interface Facets {
  change_types: Facet[];
  severities: Facet[];
  detected_by: Facet[];
  entity_types: Facet[];
}

const CHANGE_TYPE_COLORS: Record<string, string> = {
  closure_detected: "bg-red-100 text-red-800 border-red-200",
  deactivated: "bg-red-100 text-red-800 border-red-200",
  removed: "bg-red-100 text-red-800 border-red-200",
  created: "bg-green-100 text-green-800 border-green-200",
  updated: "bg-blue-100 text-blue-800 border-blue-200",
  reactivated: "bg-emerald-100 text-emerald-800 border-emerald-200",
  reappeared: "bg-emerald-100 text-emerald-800 border-emerald-200",
  unarchived: "bg-emerald-100 text-emerald-800 border-emerald-200",
  renamed: "bg-amber-100 text-amber-800 border-amber-200",
  identity_shift: "bg-amber-100 text-amber-800 border-amber-200",
  temp_closure_detected: "bg-amber-100 text-amber-800 border-amber-200",
  archived: "bg-gray-200 text-gray-700 border-gray-300",
  merged: "bg-purple-100 text-purple-800 border-purple-200",
  chain_linked: "bg-indigo-100 text-indigo-800 border-indigo-200",
  chain_changed: "bg-indigo-100 text-indigo-800 border-indigo-200",
  address_changed: "bg-blue-100 text-blue-800 border-blue-200",
};

function changeTypeColor(t: string): string {
  return CHANGE_TYPE_COLORS[t] || "bg-gray-100 text-gray-700 border-gray-200";
}

function severityColor(s: string): string {
  return s === "high"
    ? "bg-red-100 text-red-800 border-red-200"
    : "bg-gray-100 text-gray-700 border-gray-200";
}

const SOURCE_BADGE: Record<string, string> = {
  wtb: "bg-sky-100 text-sky-800 border-sky-200",
  cm: "bg-violet-100 text-violet-800 border-violet-200",
  gbp: "bg-orange-100 text-orange-800 border-orange-200",
  manual: "bg-gray-100 text-gray-700 border-gray-200",
  system: "bg-gray-100 text-gray-700 border-gray-200",
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const t = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  const day = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(d);
  return `${t} · ${day}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const PAGE_SIZE = 50;

type TabKey = "system" | "wtb" | "cm" | "uploads";

const TABS: { key: TabKey; label: string }[] = [
  { key: "system", label: "System" },
  { key: "wtb", label: "My WTB" },
  { key: "cm", label: "My Customer Master" },
  { key: "uploads", label: "Upload History" },
];

export default function Changes({ onViewed }: { onViewed?: () => void } = {}) {
  // PR #66: defense-in-depth. A brand session must never see this canonical
  // admin view — App.tsx's synchronous route guard should redirect first, but if
  // it doesn't (stale bundle, direct mount) bounce to the brand-scoped changes
  // page before any admin data loads.
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j) return;
        if (j.user?.role !== "admin" && j.tenant?.slug) {
          window.location.replace(`#/brand/${j.tenant.slug}/changes`);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const [tab, setTab] = useState<TabKey>("system");
  const [uploadOpen, setUploadOpen] = useState(false);
  // Bump to force the active tenant tab to refetch after a successful upload.
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Changes</h2>
          <p className="text-sm text-gray-600 mt-1">
            Canonical system feed plus your own WTB / Customer Master upload history.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)}>Upload</Button>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? "border-accent-500 text-accent-700"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "system" && <SystemChanges onViewed={onViewed} />}
      {tab === "wtb" && <TenantChanges key={`wtb-${refreshKey}`} source="wtb" />}
      {tab === "cm" && <TenantChanges key={`cm-${refreshKey}`} source="cm" />}
      {tab === "uploads" && <UploadHistory key={`up-${refreshKey}`} />}

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(source) => {
          setUploadOpen(false);
          setRefreshKey((k) => k + 1);
          setTab(source);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tenant change feed (PR53): /api/tenant/changes?source=wtb|cm with sub-filter
// chips and inline suggestion accept/dismiss.
// ---------------------------------------------------------------------------
interface TenantChangeRow {
  id: string;
  tenant_store_id: string | null;
  source: string;
  change_type: string;
  changed_at: string;
  source_external_id: string | null;
  display_name_at_event: string | null;
  previous_identity: Record<string, unknown> | null;
  new_identity: Record<string, unknown> | null;
  changes: Array<{ field?: string; old?: unknown; new?: unknown }> | null;
  is_suggestion: boolean;
  suggestion_state: string | null;
  notes: string | null;
  store_display_name: string | null;
  store_status: string | null;
}

const SUB_FILTERS: { key: string; label: string }[] = [
  { key: "created", label: "created" },
  { key: "renamed", label: "renamed" },
  { key: "removed", label: "removed" },
  { key: "reappeared", label: "reappeared" },
  { key: "merged", label: "merged" },
  { key: "suggestions", label: "suggestions" },
];

function changeSnippet(r: TenantChangeRow): string | null {
  if (r.changes && r.changes.length > 0) {
    const c = r.changes[0];
    if (c.field === "display_name") return `was: ${String(c.old ?? "—")}`;
    if (c.field) return `${c.field}: ${String(c.old ?? "—")} → ${String(c.new ?? "—")}`;
  }
  const prev = r.previous_identity as { display_name?: string } | null;
  if (prev?.display_name) return `was: ${prev.display_name}`;
  if (r.notes) return r.notes;
  return null;
}

function TenantChanges({ source }: { source: "wtb" | "cm" }) {
  const { context } = useSDK();
  const toast = useToast();
  const [active, setActive] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<TenantChangeRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("source", source);
    p.set("limit", String(PAGE_SIZE));
    const types = [...active].filter((a) => a !== "suggestions");
    if (active.has("suggestions")) p.set("suggestion_state", "pending");
    if (types.length) p.set("change_type", types.join(","));
    return p;
  }, [source, active]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(context, `/api/tenant/changes?${params.toString()}`);
      setRows(r.rows || []);
      setNextCursor(r.next_cursor || null);
    } catch (err) {
      toast.push(`Failed to load changes: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [context, params, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const p = new URLSearchParams(params);
      p.set("cursor", nextCursor);
      const r = await api.get(context, `/api/tenant/changes?${p.toString()}`);
      setRows((prev) => [...prev, ...(r.rows || [])]);
      setNextCursor(r.next_cursor || null);
    } catch (err) {
      toast.push(`Failed to load more: ${(err as Error).message}`, "error");
    } finally {
      setLoadingMore(false);
    }
  };

  const act = async (id: string, action: "accept" | "dismiss") => {
    setBusyId(id);
    try {
      await api.post(context, `/api/tenant/changes/${id}/${action}`);
      toast.push(`Suggestion ${action === "accept" ? "accepted" : "dismissed"}.`, "success");
      load();
    } catch (err) {
      toast.push(`Failed to ${action}: ${(err as Error).message}`, "error");
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (k: string) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {SUB_FILTERS.map((f) => {
          const selected = active.has(f.key);
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => toggle(f.key)}
              className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                selected
                  ? "bg-accent-500 text-white border-accent-500"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-sm font-medium text-gray-800">No changes yet.</div>
            <div className="text-xs text-gray-500 mt-1">
              Upload a {source.toUpperCase()} file to start tracking changes.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Store</th>
                <th className="text-left px-4 py-2 font-medium">Change</th>
                <th className="text-left px-4 py-2 font-medium">Detail</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const name = r.store_display_name || r.display_name_at_event || "—";
                const pending = r.is_suggestion && r.suggestion_state === "pending";
                return (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-4 py-2 whitespace-nowrap" title={r.changed_at}>
                      {formatWhen(r.changed_at)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{name}</div>
                      {r.source_external_id && (
                        <div className="text-xs text-gray-400 font-mono">{r.source_external_id}</div>
                      )}
                      {r.store_status && r.store_status !== "active" && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 text-[10px] mt-0.5">
                          {r.store_status}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${changeTypeColor(r.change_type)}`}
                      >
                        {r.change_type}
                      </span>
                      {r.is_suggestion && (
                        <span className="ml-1 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px]">
                          suggestion
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">{changeSnippet(r) || "—"}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase ${SOURCE_BADGE[r.source] || SOURCE_BADGE.system}`}
                      >
                        {r.source}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {pending ? (
                        <div className="inline-flex gap-1.5">
                          <Button
                            variant="ghost"
                            onClick={() => act(r.id, "accept")}
                            disabled={busyId === r.id}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => act(r.id, "dismiss")}
                            disabled={busyId === r.id}
                          >
                            Dismiss
                          </Button>
                        </div>
                      ) : r.is_suggestion && r.suggestion_state ? (
                        <span className="text-xs text-gray-400">{r.suggestion_state}</span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <div>{rows.length > 0 ? `${rows.length} loaded` : "0 results"}</div>
          <Button variant="ghost" onClick={loadMore} disabled={!nextCursor || loadingMore || loading}>
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload-run history (PR53): /api/tenant/uploads.
// ---------------------------------------------------------------------------
interface UploadRun {
  id: string;
  source: string;
  uploaded_at: string;
  row_count: number;
  created_count: number;
  renamed_count: number;
  removed_count: number;
  reappeared_count: number;
  unchanged_count: number;
  uploaded_by_name: string | null;
}

function UploadHistory() {
  const { context } = useSDK();
  const toast = useToast();
  const [rows, setRows] = useState<UploadRun[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(context, "/api/tenant/uploads?limit=100")
      .then((r) => {
        if (alive) setRows(r.rows || []);
      })
      .catch((err) => toast.push(`Failed to load uploads: ${(err as Error).message}`, "error"))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [context, toast]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      {loading && rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-gray-600">No uploads yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">When</th>
              <th className="text-left px-4 py-2 font-medium">Source</th>
              <th className="text-right px-4 py-2 font-medium">Rows</th>
              <th className="text-right px-4 py-2 font-medium">New</th>
              <th className="text-right px-4 py-2 font-medium">Renamed</th>
              <th className="text-right px-4 py-2 font-medium">Removed</th>
              <th className="text-right px-4 py-2 font-medium">Reappeared</th>
              <th className="text-right px-4 py-2 font-medium">Unchanged</th>
              <th className="text-left px-4 py-2 font-medium">By</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 whitespace-nowrap" title={r.uploaded_at}>
                  {formatWhen(r.uploaded_at)}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase ${SOURCE_BADGE[r.source] || SOURCE_BADGE.system}`}
                  >
                    {r.source}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">{r.row_count}</td>
                <td className="px-4 py-2 text-right text-green-700">{r.created_count}</td>
                <td className="px-4 py-2 text-right text-amber-700">{r.renamed_count}</td>
                <td className="px-4 py-2 text-right text-red-700">{r.removed_count}</td>
                <td className="px-4 py-2 text-right text-emerald-700">{r.reappeared_count}</td>
                <td className="px-4 py-2 text-right text-gray-500">{r.unchanged_count}</td>
                <td className="px-4 py-2 text-gray-600 text-xs">{r.uploaded_by_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload modal (PR53): pick a source, papaparse the CSV client-side, POST the
// parsed rows to /api/tenant/uploads/<source>, show a summary toast.
// ---------------------------------------------------------------------------
function UploadModal({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: (source: "wtb" | "cm") => void;
}) {
  const { context } = useSDK();
  const toast = useToast();
  const [source, setSource] = useState<"wtb" | "cm">("wtb");
  const [fileName, setFileName] = useState<string>("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFileName("");
    setHeaders([]);
    setRows([]);
  };

  const handleFile = useCallback((f: File) => {
    setFileName(f.name);
    Papa.parse<Record<string, string>>(f, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: (out) => {
        setHeaders((out.meta.fields as string[]) || []);
        setRows((out.data as Record<string, string>[]) || []);
      },
    });
  }, []);

  const submit = async () => {
    if (rows.length === 0) {
      toast.push("Pick a CSV with at least one row.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(context, `/api/tenant/uploads/${source}`, { headers, rows });
      toast.push(
        `${r.row_count} stores · ${r.created_count} new · ${r.renamed_count} renamed · ${r.removed_count} removed`,
        "success"
      );
      reset();
      onUploaded(source);
    } catch (err) {
      toast.push(`Upload failed: ${(err as Error).message}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          reset();
          onClose();
        }
      }}
    >
      <DialogTitle>Upload store list</DialogTitle>
      <DialogDescription>
        Re-upload your WTB sheet or Customer Master CSV. We diff it against your last upload and log
        every created / renamed / removed / reappeared store.
      </DialogDescription>

      <div className="mt-4 space-y-4">
        <div className="flex gap-2">
          {(["wtb", "cm"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                source === s
                  ? "border-accent-500 bg-accent-50 text-accent-700"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {s === "wtb" ? "Where-to-Buy" : "Customer Master"}
            </button>
          ))}
        </div>

        <label className="block">
          <span className="text-xs text-gray-500">CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-accent-500 file:px-3 file:py-1.5 file:text-white"
          />
        </label>

        {fileName && (
          <div className="text-xs text-gray-500">
            {fileName} — {rows.length} rows, {headers.length} columns
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || rows.length === 0}>
            {submitting ? "Uploading…" : "Upload & diff"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// System change feed — the original admin canonical feed (core.entity_change_log),
// preserved verbatim as the default "System" tab.
// ---------------------------------------------------------------------------
function SystemChanges({ onViewed }: { onViewed?: () => void }) {
  const { context } = useSDK();
  const toast = useToast();
  const viewedRef = useRef(false);

  const [from, setFrom] = useState<string>(daysAgoIso(30));
  const [to, setTo] = useState<string>(todayIso());
  const [types, setTypes] = useState<string[]>([]);
  const [severities, setSeverities] = useState<string[]>([]);
  const [detectedBy, setDetectedBy] = useState<string>("");
  const [entityType, setEntityType] = useState<string>("");
  const [gbpOnly, setGbpOnly] = useState<boolean>(false);
  const [q, setQ] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");

  const [rows, setRows] = useState<ChangeRow[]>([]);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Debounce free-text into the committed `q` used by the query string.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(qInput.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [qInput]);

  const baseParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (types.length) params.set("types", types.join(","));
    if (severities.length) params.set("severities", severities.join(","));
    if (detectedBy) params.set("detected_by", detectedBy);
    if (entityType) params.set("entity_types", entityType);
    if (gbpOnly) params.set("gbp_only", "true");
    if (q) params.set("q", q);
    return params;
  }, [from, to, types, severities, detectedBy, entityType, gbpOnly, q]);

  const loadFirst = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(context, `/api/admin/changes?${baseParams.toString()}`);
      setRows(r.rows || []);
      setNextCursor(r.next_cursor || null);
      setHasMore(Boolean(r.has_more));
      // Clear the nav "unread" badge once, after the first successful load — a
      // failed page must not mark changes as viewed.
      if (!viewedRef.current) {
        viewedRef.current = true;
        onViewed?.();
      }
    } catch (err) {
      toast.push(`Failed to load changes: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [context, baseParams, toast, onViewed]);

  const loadFacets = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      const r = await api.get(context, `/api/admin/changes/facets?${p.toString()}`);
      setFacets(r);
    } catch {
      // Facets are non-critical; the filter chips still work without counts.
    }
  }, [context, from, to]);

  // Initial load + on filter change: fetch page 1. Facets only depend on range.
  useEffect(() => {
    loadFirst();
  }, [loadFirst]);
  useEffect(() => {
    loadFacets();
  }, [loadFacets]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const p = new URLSearchParams(baseParams);
      p.set("cursor", nextCursor);
      const r = await api.get(context, `/api/admin/changes?${p.toString()}`);
      setRows((prev) => [...prev, ...(r.rows || [])]);
      setNextCursor(r.next_cursor || null);
      setHasMore(Boolean(r.has_more));
    } catch (err) {
      toast.push(`Failed to load more: ${(err as Error).message}`, "error");
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleType = (v: string) =>
    setTypes((prev) => (prev.includes(v) ? prev.filter((t) => t !== v) : [...prev, v]));
  const toggleSeverity = (v: string) =>
    setSeverities((prev) => (prev.includes(v) ? prev.filter((s) => s !== v) : [...prev, v]));

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const reset = () => {
    setFrom(daysAgoIso(30));
    setTo(todayIso());
    setTypes([]);
    setSeverities([]);
    setDetectedBy("");
    setEntityType("");
    setGbpOnly(false);
    setQ("");
    setQInput("");
  };

  const typeOptions = facets?.change_types ?? [];
  const severityOptions = facets?.severities ?? [{ value: "normal", count: 0 }, { value: "high", count: 0 }];

  return (
    <div className="space-y-4">
      <div className="sticky top-[52px] z-10 rounded-lg border border-gray-200 bg-white/95 backdrop-blur p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-2 py-1"
            />
          </label>
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-2 py-1"
            />
          </label>
          <select
            value={detectedBy}
            onChange={(e) => setDetectedBy(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1"
          >
            <option value="">All sources</option>
            {(facets?.detected_by ?? []).map((d) => (
              <option key={d.value} value={d.value}>
                {d.value} ({d.count})
              </option>
            ))}
          </select>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1"
          >
            <option value="">All entity types</option>
            {(facets?.entity_types ?? []).map((d) => (
              <option key={d.value} value={d.value}>
                {d.value} ({d.count})
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setGbpOnly((v) => !v)}
            aria-pressed={gbpOnly}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
              gbpOnly
                ? "bg-accent-500 text-white border-accent-500"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
          >
            GBP only
          </button>
          <input
            type="text"
            placeholder="Search license, diff, notes…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1 w-60"
          />
          <Button variant="ghost" onClick={reset}>
            Reset
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {typeOptions.map((o) => {
            const selected = types.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggleType(o.value)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  selected
                    ? "bg-accent-500 text-white border-accent-500"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {o.value} <span className="opacity-60">{o.count}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {severityOptions.map((o) => {
            const selected = severities.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggleSeverity(o.value)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  selected
                    ? "bg-accent-500 text-white border-accent-500"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                severity: {o.value} <span className="opacity-60">{o.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">
              ○
            </div>
            <div className="text-sm font-medium text-gray-800">No changes match your filters.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Entity</th>
                <th className="text-left px-4 py-2 font-medium">Change</th>
                <th className="text-left px-4 py-2 font-medium">Severity</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-left px-4 py-2 font-medium w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const id = String(r.change_id);
                const isOpen = expanded.has(id);
                return (
                  <Fragment key={id}>
                    <tr className="border-t border-gray-100 hover:bg-gray-50 align-top">
                      <td className="px-4 py-2 whitespace-nowrap" title={r.changed_at}>
                        {formatWhen(r.changed_at)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-mono text-xs font-semibold text-gray-900">
                            {r.license_number || "—"}
                          </span>
                          {r.entity_type && (
                            <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 text-[10px]">
                              {r.entity_type}
                            </span>
                          )}
                          {r.is_gbp_change && (
                            <span
                              className="inline-flex items-center rounded-full bg-accent-500 text-white border border-accent-500 px-2 py-0.5 text-[10px] font-semibold"
                              title="Google Business Profile signal"
                            >
                              GBP
                            </span>
                          )}
                          {r.gbp_changed_fields?.map((field) => (
                            <span
                              key={field}
                              className="inline-flex items-center rounded-full bg-accent-50 text-accent-700 border border-accent-100 px-2 py-0.5 text-[10px] font-mono"
                            >
                              {field}
                            </span>
                          ))}
                        </div>
                        {(() => {
                          const primary = displayLicenseName(r.display_name, r.dba_name, r.legal_name);
                          if (!primary) return null;
                          const legal = r.legal_name?.trim() || null;
                          const showLegal = legal && legal !== primary;
                          return (
                            <div className="text-xs text-gray-500">
                              {primary}
                              {showLegal ? ` · ${legal}` : ""}
                            </div>
                          );
                        })()}
                        {r.entity_type === "gbp_profile" && r.place_id && (
                          <div className="text-xs text-gray-400 font-mono">place_id: {r.place_id}</div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${changeTypeColor(r.change_type)}`}
                        >
                          {r.change_type}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${severityColor(r.severity)}`}
                        >
                          {r.severity}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-700 text-xs">{r.detected_by || "—"}</td>
                      <td className="px-4 py-2">
                        <button
                          className="text-gray-400 hover:text-gray-700"
                          onClick={() => toggleExpanded(id)}
                          aria-label={isOpen ? "Collapse" : "Expand"}
                        >
                          {isOpen ? "▾" : "▸"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50 border-t border-gray-100">
                        <td colSpan={6} className="px-4 py-3">
                          {r.notes && <div className="text-xs text-gray-600 mb-2">{r.notes}</div>}
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
{JSON.stringify(r.diff ?? {}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <div>{rows.length > 0 ? `${rows.length} loaded` : "0 results"}</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={loadMore} disabled={!hasMore || loadingMore || loading}>
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
